import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Sparkles, AlertTriangle, Rocket } from "lucide-react";
import { Reveal, RevealItem } from "../components/Reveal";
import { Tilt } from "../components/Tilt";
import { MagneticButton } from "../components/MagneticButton";
import { WaitlistButton } from "../components/WaitlistButton";
import { getJSON, postJSON } from "../lib/api";
import { useAuthStore } from "../state/authStore";
import { usePageMeta } from "../lib/usePageMeta";
import { SEO } from "../components/SEO";
import { Breadcrumb } from "../components/Breadcrumb";
import { BREADCRUMBS } from "../lib/pageMetadata";

// Pre-launch gate. When true, paid-tier CTAs are replaced with WaitlistButton
// for everyone EXCEPT users with role="admin" — Luca can still Stripe-test.
const WAITLIST_MODE = String(process.env.REACT_APP_WAITLIST_MODE || "").toLowerCase() === "true";

function WaitlistForm({ tierKey }) {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState("idle");
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim() || status === "loading") return;
        setStatus("loading");
        setErr("");
        try {
            await postJSON("/waitlist", { email: email.trim(), tier: tierKey });
            setStatus("success");
        } catch (e2) {
            setStatus("error");
            setErr(e2.message || "Could not save email.");
        }
    };

    if (status === "success") {
        return (
            <div
                className="mt-3 inline-flex items-center gap-2 text-[12.5px] text-accent-mint"
                data-testid={`waitlist-success-${tierKey}`}
            >
                <Check className="w-3.5 h-3.5" />
                Saved. We&apos;ll email you when this tier launches.
            </div>
        );
    }

    return (
        <form
            onSubmit={submit}
            className="mt-3 flex items-stretch gap-2"
            data-testid={`waitlist-form-${tierKey}`}
        >
            <input
                type="email"
                required
                placeholder="you@work.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-canvas/60 border border-white/[0.07] rounded-md px-3 py-2 text-[13px] text-text-primary placeholder:text-text-dim outline-none focus:border-accent-cyan/50 transition-colors duration-200"
                data-testid={`waitlist-email-${tierKey}`}
            />
            <button
                type="submit"
                disabled={status === "loading"}
                className="px-3.5 py-2 rounded-md bg-accent-cyan text-canvas text-[12.5px] font-medium hover:shadow-glow-cyan disabled:opacity-40 transition-shadow duration-200"
                data-testid={`waitlist-submit-${tierKey}`}
            >
                {status === "loading" ? "…" : "Notify me"}
            </button>
            {status === "error" && (
                <p className="mt-2 text-[11px] text-accent-red" data-testid={`waitlist-error-${tierKey}`}>
                    {err}
                </p>
            )}
        </form>
    );
}

export const Pricing = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const upgradeRequired = params.get("upgrade") === "required";
    const canceled = params.get("canceled") === "1";
    const { user } = useAuthStore();
    const [openTier, setOpenTier] = useState(null);
    const [busyTier, setBusyTier] = useState(null);

    usePageMeta({
        title: "Pricing — Simple plans, no surprises.",
        description:
            "Free and Pro plans. ZeroConf ZTP multi-vendor firewall wizard, CVE lookup, DNS health, and managed support — pick the tier that fits your team.",
    });

    const isPaying = user && ["pro", "msp"].includes(user.subscription_plan) && ["active", "trialing"].includes(user.subscription_status);
    const currentPlan = user?.subscription_plan;

    // Plans are fetched from /api/billing/plans so the PUBLIC_TIERS env flag
    // (server-side) can hide tiers without a frontend deploy.
    const [tiers, setTiers] = useState(null);
    const [tiersErr, setTiersErr] = useState("");

    useEffect(() => {
        let cancelled = false;
        getJSON("/billing/plans")
            .then((r) => {
                if (!cancelled) setTiers(r?.plans || []);
            })
            .catch((e) => {
                if (!cancelled) setTiersErr(e?.message || "Could not load plans");
            });
        return () => { cancelled = true; };
    }, []);

    const startCheckout = async (tierKey) => {
        if (busyTier) return;
        setBusyTier(tierKey);
        try {
            // The pro_monthly card pre-applies the founding coupon by default
            // (UI promise mirrors the env-driven discount). pro_annual ignores
            // the flag server-side, so it's safe to always send.
            const applyFounding =
                tierKey === "pro_monthly" && !foundingDismissed;
            const r = await postJSON("/billing/checkout", {
                plan: tierKey,
                apply_founding_coupon: applyFounding,
            });
            if (!r?.url) return;
            // Real Stripe returns an absolute https URL — full-page navigate
            // so the user lands on Stripe Checkout. The mock provider returns
            // a relative path; both work through window.location.href.
            if (/^https?:\/\//i.test(r.url)) {
                window.location.href = r.url;
            } else {
                navigate(r.url);
            }
        } catch (e) {
            console.error(e);
            setBusyTier(null);
        }
    };

    // Founding banner: dismissable, persists for 30 days via localStorage.
    const FOUNDING_DISMISS_KEY = "cf:founding-banner-dismissed-until";
    const [foundingDismissed, setFoundingDismissed] = useState(false);
    useEffect(() => {
        try {
            const until = parseInt(localStorage.getItem(FOUNDING_DISMISS_KEY) || "0", 10);
            setFoundingDismissed(until > Date.now());
        } catch {
            setFoundingDismissed(false);
        }
    }, []);
    const dismissFounding = () => {
        try {
            localStorage.setItem(
                FOUNDING_DISMISS_KEY,
                String(Date.now() + 30 * 24 * 60 * 60 * 1000),
            );
        } catch {
            /* localStorage disabled — banner re-appears on reload */
        }
        setFoundingDismissed(true);
    };

    // Pull the founding promo block off the pro_monthly tier so the banner can
    // surface it without hard-coding numbers in JSX.
    const foundingPromo = (tiers || []).find((t) => t.id === "pro_monthly")?.founding || null;

    const renderCTA = (tier) => {
        // Free tier
        if (tier.id === "free") {
            if (user) {
                return (
                    <MagneticButton
                        type="button"
                        onClick={() => navigate("/dashboard")}
                        className="w-full py-2.5 rounded-md bg-elevated/60 text-text-primary border border-white/[0.08] text-[13px] font-medium hover:border-accent-cyan/40 hover:text-accent-cyan transition-colors duration-200"
                        data-testid={`pricing-cta-${tier.id}`}
                        ariaLabel="Open dashboard"
                    >
                        Already free — open dashboard
                    </MagneticButton>
                );
            }
            if (openTier === tier.id) {
                return <WaitlistForm tierKey={tier.id} />;
            }
            return (
                <MagneticButton
                    type="button"
                    onClick={() => setOpenTier(tier.id)}
                    className="w-full py-2.5 rounded-md bg-elevated/60 text-text-primary border border-white/[0.08] text-[13px] font-medium hover:border-accent-cyan/40 hover:text-accent-cyan transition-colors duration-200"
                    data-testid={`pricing-cta-${tier.id}`}
                    ariaLabel={tier.cta_label}
                >
                    {tier.cta_label}
                </MagneticButton>
            );
        }

        // Paid tier
        const isAdmin = user?.role === "admin";

        // Pre-launch waitlist gate — applies to ANY visitor that isn't admin,
        // logged in or not. Catches the most common upgrade entry point.
        if (WAITLIST_MODE && !isAdmin) {
            return (
                <WaitlistButton
                    source={`pricing:${tier.id}`}
                    variant={tier.recommended ? "primary" : "secondary"}
                    dataTestId={`pricing-waitlist-${tier.id}`}
                >
                    Get notified when Pro launches
                </WaitlistButton>
            );
        }

        if (!user) {
            return (
                <MagneticButton
                    type="button"
                    onClick={() => navigate(`/login?next=${encodeURIComponent("/pricing")}`)}
                    className={`w-full py-2.5 rounded-md text-[13px] font-medium transition-all duration-200 ${
                        tier.recommended
                            ? "bg-accent-cyan text-canvas hover:shadow-glow-cyan"
                            : "bg-elevated/60 text-text-primary border border-white/[0.08] hover:border-accent-cyan/40 hover:text-accent-cyan"
                    }`}
                    data-testid={`pricing-cta-${tier.id}`}
                    ariaLabel="Sign in to upgrade"
                >
                    Sign in to upgrade
                </MagneticButton>
            );
        }

        if (currentPlan === tier.id && ["active", "trialing"].includes(user.subscription_status)) {
            return (
                <button
                    type="button"
                    disabled
                    className="w-full py-2.5 rounded-md bg-elevated/40 border border-accent-mint/30 text-[13px] text-accent-mint font-medium cursor-default"
                    data-testid={`pricing-current-${tier.id}`}
                >
                    Current plan
                </button>
            );
        }

        return (
            <div className="space-y-1.5">
                <MagneticButton
                    type="button"
                    onClick={() => startCheckout(tier.id)}
                    disabled={busyTier === tier.id}
                    className={`w-full py-2.5 rounded-md text-[13px] font-medium transition-all duration-200 disabled:opacity-40 ${
                        tier.recommended
                            ? "bg-accent-cyan text-canvas hover:shadow-glow-cyan"
                            : "bg-elevated/60 text-text-primary border border-white/[0.08] hover:border-accent-cyan/40 hover:text-accent-cyan"
                    }`}
                    data-testid={`pricing-cta-${tier.id}`}
                    ariaLabel={tier.cta_label}
                >
                    {busyTier === tier.id ? "Redirecting…" : tier.cta_label}
                </MagneticButton>
                {WAITLIST_MODE && isAdmin && (
                    <p
                        className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-accent-amber text-center"
                        data-testid={`pricing-admin-badge-${tier.id}`}
                    >
                        Admin test mode — using Sandbox
                    </p>
                )}
            </div>
        );
    };

    // Grid columns scale with the number of public tiers (1 ⇒ centered, 2 ⇒
    // wide pair, 3 ⇒ classic). Keeps the layout balanced when MSP is hidden.
    const tierCount = tiers ? tiers.length : 0;
    const gridCols =
        tierCount >= 3
            ? "md:grid-cols-3"
            : tierCount === 2
                ? "md:grid-cols-2 max-w-4xl mx-auto"
                : "md:grid-cols-1 max-w-md mx-auto";
    const titleSuffix = tierCount === 2 ? "Two tiers." : tierCount === 1 ? "Simple pricing." : "Three tiers.";

    return (
        <div className="pt-24 md:pt-28 pb-6 mx-auto max-w-7xl px-5 md:px-10" data-testid="pricing-page">
            <Breadcrumb items={BREADCRUMBS.pricing} />
            <SEO
                title="Pricing — Free, Pro, MSP"
                description="Predictable pricing for the chat-first firewall configurator and Client IPAM. Free forever for tools and concierge chat. Pro at $19.90/mo. MSP plan available on request."
                canonicalPath="/pricing"
            />
            {/* Pre-launch banner — visible to everyone except admins. */}
            {WAITLIST_MODE && user?.role !== "admin" && (
                <Reveal>
                    <div
                        className="mb-6 rounded-md border border-accent-cyan/40 bg-accent-cyan/[0.06] px-4 py-3 text-[13px] text-text-primary flex items-start gap-3"
                        data-testid="pricing-waitlist-banner"
                    >
                        <Rocket className="w-4 h-4 text-accent-cyan mt-0.5 shrink-0" />
                        <p className="leading-relaxed">
                            <span className="text-accent-cyan font-medium">
                                Pro tier is launching soon.
                            </span>{" "}
                            Join the waitlist to get <strong>$10 off the first 3 months</strong>{" "}
                            as a Founding Member.
                        </p>
                    </div>
                </Reveal>
            )}
            {upgradeRequired && (
                <Reveal>
                    <div
                        className="mb-6 rounded-md border border-accent-amber/40 bg-accent-amber/[0.06] px-3 py-2.5 text-[13px] text-accent-amber flex items-center gap-2"
                        data-testid="pricing-upgrade-banner"
                    >
                        <AlertTriangle className="w-3.5 h-3.5" />
                        ZeroConf is part of our Pro plan. Pick a tier below to continue.
                    </div>
                </Reveal>
            )}
            {canceled && (
                <Reveal>
                    <div
                        className="mb-6 rounded-md border border-white/[0.08] bg-elevated/40 px-3 py-2.5 text-[13px] text-text-muted flex items-center gap-2"
                        data-testid="pricing-canceled-banner"
                    >
                        Checkout canceled — no charge was made.
                    </div>
                </Reveal>
            )}

            <Reveal>
                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                    Pricing
                </p>
                <h1
                    className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tight leading-[1.05] max-w-3xl"
                    data-testid="pricing-title"
                >
                    Simple pricing. <span className="text-accent-cyan">No surprises.</span>
                </h1>
                <p className="mt-5 text-text-muted max-w-2xl leading-relaxed">
                    Pro is live and billable today — start a real Stripe checkout, cancel
                    any time. We never store your card; Stripe does.
                </p>
            </Reveal>

            {!foundingDismissed && foundingPromo && (
                <Reveal>
                    <div
                        className="mt-8 rounded-xl border border-accent-mint/30 bg-accent-mint/[0.06] px-4 py-3 text-[13px] text-text-primary flex items-start gap-3 relative"
                        data-testid="pricing-founding-banner"
                    >
                        <Sparkles className="w-4 h-4 text-accent-mint mt-0.5 shrink-0" />
                        <p className="flex-1 leading-relaxed">
                            <span className="font-semibold text-accent-mint">Founding Member launch</span>
                            {" — "}
                            First 100 sign-ups get{" "}
                            <span className="text-accent-mint font-mono">
                                ${foundingPromo.discounted_amount.toFixed(2)}
                            </span>
                            /mo for {foundingPromo.duration_months} months, then $
                            {((foundingPromo.discounted_amount + foundingPromo.discount_amount) || 19.9).toFixed(2)}
                            /mo. Auto-applied at checkout (or use code{" "}
                            <code className="font-mono text-accent-mint">{foundingPromo.code}</code>
                            ).
                        </p>
                        <button
                            type="button"
                            onClick={dismissFounding}
                            className="text-text-dim hover:text-text-primary text-[18px] leading-none shrink-0 transition-colors"
                            aria-label="Dismiss founding banner"
                            data-testid="pricing-founding-dismiss"
                        >
                            ×
                        </button>
                    </div>
                </Reveal>
            )}

            {tiersErr && (
                <p
                    className="mt-8 text-[12px] text-accent-red"
                    data-testid="pricing-load-error"
                >
                    Could not load plans: {tiersErr}
                </p>
            )}

            <Reveal stagger className={`mt-14 grid gap-5 items-stretch ${gridCols}`}>
                {(tiers || []).map((tier) => (
                    <RevealItem key={tier.id} className="h-full">
                        <Tilt
                            className={`relative rounded-2xl border bg-surface/60 backdrop-blur-md p-6 md:p-7 h-full flex flex-col overflow-hidden group ${
                                tier.recommended
                                    ? "border-accent-cyan/40 shadow-glow-cyan"
                                    : "border-white/[0.06]"
                            }`}
                            data-testid={`pricing-card-${tier.id}`}
                        >
                            {tier.recommended && (
                                <span
                                    className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.22em] px-2 py-1 rounded-md border border-accent-cyan/40 text-accent-cyan bg-accent-cyan/5"
                                    data-testid="pricing-popular-badge"
                                >
                                    <Sparkles className="w-3 h-3" />
                                    Most popular
                                </span>
                            )}
                            <div>
                                <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-text-dim">
                                    {tier.code}
                                </p>
                                <h3 className="mt-2 text-2xl font-semibold text-text-primary tracking-tight">
                                    {tier.name}
                                </h3>
                                <p className="mt-2 text-[13px] text-text-muted leading-relaxed min-h-[42px]">
                                    {tier.tagline}
                                </p>
                                <div className="mt-5 flex items-baseline gap-1.5">
                                    <span className="font-display font-semibold text-4xl text-text-primary tracking-tight">
                                        {tier.price_label}
                                    </span>
                                    <span className="text-[12px] text-text-muted">
                                        {tier.billing_period}
                                    </span>
                                </div>
                                {tier.id === "pro_monthly" && tier.founding && !foundingDismissed && (
                                    <p
                                        className="mt-2 text-[11.5px] text-accent-mint leading-snug"
                                        data-testid="pricing-card-founding-line"
                                    >
                                        or{" "}
                                        <span className="font-mono">
                                            ${tier.founding.discounted_amount.toFixed(2)}/mo
                                        </span>{" "}
                                        for {tier.founding.duration_months} months with code{" "}
                                        <code className="font-mono">{tier.founding.code}</code>
                                    </p>
                                )}
                                {tier.id === "pro_annual" && tier.badge && (
                                    <p
                                        className="mt-2 text-[11.5px] text-accent-mint leading-snug font-mono"
                                        data-testid="pricing-card-annual-badge"
                                    >
                                        {tier.badge}
                                    </p>
                                )}
                            </div>

                            <ul className="mt-6 space-y-2.5 flex-1">
                                {tier.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2 text-[13px] text-text-muted">
                                        <Check className="w-3.5 h-3.5 text-accent-cyan mt-0.5 shrink-0" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-6">{renderCTA(tier)}</div>
                        </Tilt>
                    </RevealItem>
                ))}
            </Reveal>

            <Reveal className="mt-14">
                <p className="text-[12px] text-text-dim text-center max-w-xl mx-auto">
                    Need a larger MSP / multi-tenant setup or a custom plan? Email{" "}
                    <a href="mailto:info@cybersecurityforefront.com" className="text-accent-cyan hover:underline">
                        info@cybersecurityforefront.com
                    </a>{" "}
                    and we&apos;ll size something to your environment.
                </p>
            </Reveal>
        </div>
    );
};

export default Pricing;
